import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    ListItemText,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import api from '../../../../config/axiosConfig';
import { API } from '../../../../utils/apiRoutes';
import { formatJalaliDateTime } from '../../../../utils/formatJalaliDate';
import { toEnglishDigits, toPersianDigits } from '../../../../utils/convertNumbers';
import { sendErrorMessage, useAssistant } from '../../../../context/AssistantContext';
import { ConversationMenu } from '../../../Sidebar/AssistantNav';

const SEARCH_DEBOUNCE_MS = 350;

// «همهٔ گفتگوها»: the full, server-paginated and server-searched list of this
// user's threads in this institute. Shown in place of the chat (not as a
// second permanent column).
function AssistantHistory() {
    const { listVersion, requestRename, requestDelete } = useAssistant();
    const [query, setQuery] = useState('');
    const [debounced, setDebounced] = useState('');
    const [state, setState] = useState({ items: [], next: null, count: 0, loading: true, error: null });
    const [loadingMore, setLoadingMore] = useState(false);
    const [menu, setMenu] = useState({ anchor: null, conversation: null });
    const tokenRef = useRef(0);

    useEffect(() => {
        const timer = setTimeout(() => setDebounced(query.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [query]);

    useEffect(() => {
        const token = ++tokenRef.current;
        const controller = new AbortController();
        setState((prev) => ({ ...prev, loading: true, error: null }));
        api.get(API.assistantConversations, {
            params: debounced ? { search: toEnglishDigits(debounced) } : {},
            signal: controller.signal,
        })
            .then((res) => {
                if (token !== tokenRef.current) return;
                setState({
                    items: res.data?.results || [],
                    next: res.data?.next || null,
                    count: res.data?.count ?? (res.data?.results || []).length,
                    loading: false,
                    error: null,
                });
            })
            .catch((err) => {
                if (token !== tokenRef.current || err?.name === 'CanceledError') return;
                setState((prev) => ({ ...prev, loading: false, error: sendErrorMessage(err, 'فهرست گفتگوها دریافت نشد.') }));
            });
        return () => controller.abort();
    }, [debounced, listVersion]);

    const loadMore = async () => {
        if (!state.next || loadingMore) return;
        const token = tokenRef.current;
        setLoadingMore(true);
        try {
            // Only the page number is taken from `next` (its host/scheme may
            // not match behind the proxy); the search is re-sent as-is.
            const page = new URL(state.next, window.location.origin).searchParams.get('page');
            const res = await api.get(API.assistantConversations, {
                params: { page, ...(debounced ? { search: toEnglishDigits(debounced) } : {}) },
            });
            if (token !== tokenRef.current) return;
            setState((prev) => {
                const ids = new Set(prev.items.map((c) => c.id));
                return {
                    ...prev,
                    items: [...prev.items, ...(res.data?.results || []).filter((c) => !ids.has(c.id))],
                    next: res.data?.next || null,
                };
            });
        } catch (err) {
            setState((prev) => ({ ...prev, error: sendErrorMessage(err, 'ادامهٔ فهرست دریافت نشد.') }));
        } finally {
            setLoadingMore(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
                <Typography component="h1" sx={{ fontWeight: 700, fontSize: '1rem', m: 0 }}>
                    همهٔ گفتگوها
                </Typography>
                {!state.loading && (
                    <Typography variant="caption" color="text.secondary" aria-live="polite">
                        {toPersianDigits(`${state.count} گفتگو`)}
                    </Typography>
                )}
                <TextField
                    size="small"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="جست‌وجو در عنوان و متن گفتگوها"
                    inputProps={{ 'aria-label': 'جست‌وجوی گفتگوها' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ flex: '1 1 220px', '& .MuiInputBase-root': { height: 40 } }}
                />
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 1, sm: 2 }, py: 1 }}>
                {state.error && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        {state.error}
                    </Alert>
                )}
                {state.loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress size={24} aria-label="در حال بارگذاری" />
                    </Box>
                ) : state.items.length === 0 && !state.error ? (
                    <Typography align="center" variant="body2" color="text.secondary" sx={{ p: 2 }}>
                        {debounced ? 'گفتگویی با این عبارت پیدا نشد.' : 'هنوز گفتگویی ندارید.'}
                    </Typography>
                ) : (
                    <List disablePadding>
                        {state.items.map((conversation) => {
                            const title = conversation.title || 'بدون عنوان';
                            return (
                                <Box
                                    key={conversation.id}
                                    component="li"
                                    sx={{ display: 'flex', alignItems: 'center', listStyle: 'none', borderBottom: 1, borderColor: 'divider' }}
                                >
                                    <ListItemButton
                                        component={Link}
                                        to={`/dashboard/assistant?conversation=${conversation.id}`}
                                        sx={{ flex: 1, minWidth: 0, borderRadius: 1 }}
                                    >
                                        <ListItemText
                                            primary={toPersianDigits(title)}
                                            secondary={
                                                <>
                                                    {conversation.last_message && (
                                                        <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {toPersianDigits(conversation.last_message)}
                                                        </Box>
                                                    )}
                                                    <Box component="span" sx={{ fontSize: '0.7rem' }}>
                                                        {toPersianDigits(formatJalaliDateTime(conversation.updated_at))}
                                                    </Box>
                                                </>
                                            }
                                            primaryTypographyProps={{ noWrap: true, fontWeight: 600, title }}
                                        />
                                    </ListItemButton>
                                    <IconButton
                                        aria-label={`گزینه‌های گفتگوی «${title}»`}
                                        aria-haspopup="menu"
                                        onClick={(event) => setMenu({ anchor: event.currentTarget, conversation })}
                                    >
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            );
                        })}
                    </List>
                )}
                {state.next && !state.loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                        <Button variant="outlined" onClick={loadMore} disabled={loadingMore}>
                            {loadingMore ? <CircularProgress size={18} /> : 'نمایش گفتگوهای بیشتر'}
                        </Button>
                    </Box>
                )}
            </Box>

            <ConversationMenu
                anchorEl={menu.anchor}
                conversation={menu.conversation}
                onClose={() => setMenu({ anchor: null, conversation: null })}
                onRename={requestRename}
                onDelete={requestDelete}
            />
        </Box>
    );
}

export default AssistantHistory;
